import React, { useState, useEffect } from "react";

import ChatHeader from './ChatHeader';
import ChatNewMessage from './ChatNewMessage';
import ChatMessage from './ChatMessages';
import ChatLogin from './ChatLogin';
import ChatGiveMessage from './ChatGiveMessage';
import ChatMessageGiven from './ChatMessageGiven';
import ChatEvaluation from './ChatEvaluation';
import ChatThankYou from './ChatThankYou';
import TenantUnavailable from './TenantUnavailable';

import styles from './Chat.module.css';

import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from "react-redux";
import { selectRoomInfo } from "../../redux/room/room.actions";
import { selectTenantError } from "../../redux/tenant/tenant.actions";
import Spinner from "./Spinner/spinner";
import { setLoading, setNotLoading } from "../../redux/loading/loading.actions";
import { guardarSaudacao } from "../../utils/saudacaoDoChat";
import httpClient from "../../assets/api/http-client";
import { setTenantSlug } from "../../utils/tenant";

const ChatRoom = (props) => {
  // Resolve o slug do tenant a partir da rota (`/:slug` ou `/`, sem slug
  // -> cai no padrão configurável -- ver utils/tenant.js) SINCRONAMENTE no
  // corpo do componente, e não num useEffect: efeitos de filhos (ex.: o
  // fetch de departamentos do ChatLogin) rodam ANTES do efeito do pai, então
  // só setar isso durante o render garante que toda chamada à API já saia
  // com o slug certo, mesmo a primeiríssima.
  const slugParam = props.match && props.match.params ? props.match.params.slug : undefined;
  setTenantSlug(slugParam);

  const tenantError = useSelector(selectTenantError);

  const [sendNewMessage, setSendNewMessage] = useState(false);
  /*
   * Quantas teclas o cliente apertou (Gisele, 23/10/2026).
   *
   * Era `sendingMessage`, um booleano que o `keypress` INVERTIA -- metade das
   * teclas não avisava nada, e quando o cliente parava a flag ficava ligada
   * para sempre, porque ninguém a desligava. Com ela presa em ligado, o efeito
   * que emite o sinal (que depende de `messages`) reemitia "digitando" a cada
   * mensagem nova: o operador respondia e via o cliente "digitando" sem o
   * cliente ter tocado no teclado.
   *
   * Contagem não tem esse problema: ela só muda quando alguém digita.
   */
  const [teclasDigitadas, setTeclasDigitadas] = useState(0);
  const [newMessage, setNewMessage] = useState(""); // Message to be sent
  const [sendingFile, setSendingFile] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [nameError, setNameError] = useState('');
  const [disableChat, setDisableChat] = useState(false)
  const [showEndChatPrompt, setShowEndChatPrompt] = useState(false);
  const [outOfHoursMessage, setOutOfHoursMessage] = useState('');
  const roomInfo = useSelector(selectRoomInfo);
  const dispatch = useDispatch();

  const initializeState = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    console.log('initializeState - user from localStorage:', user);
    
    if (user) {
      const createdAt = new Date(user.room.created_at);
      const now = new Date();
      const diffInHours = Math.abs(now - createdAt) / 36e5;
      console.log('Session age in hours:', diffInHours);
      
      if (diffInHours > 12) {
        alert('A sessão do chat expirou!');
        localStorage.clear();
        return {
          user: null,
          status: 3,
          roomId: null,
        };
      } else {
        console.log('Valid session, roomId:', user.room.room_id);
        return {
          user,
          status: 1,
          roomId: user.room.room_id,
        };
      }
    }
    console.log('No user found in localStorage');
    return { status: 3 };
  };

  const { user: initialUser, status: initialStatus, roomId: initialRoomId } = initializeState();
  const [status, setStatus] = useState(initialStatus);
  const [user, setUser] = useState(initialUser);
  const [roomId, setRoomId] = useState(initialRoomId);

  // Debug logs
  console.log('ChatRoom initialized with:', { initialUser, initialStatus, initialRoomId });
  const CRM_id = JSON.parse(localStorage.getItem('roomInfo'))?.CRM_id || '';
  const operatorId = JSON.parse(localStorage.getItem('roomInfo'))?.operatorId || '';

  const handleSendingMessage = () => {
    // Atualização funcional: o ouvinte de `keypress` é registrado uma vez e
    // carregaria o valor do primeiro render para sempre. Com `n => n + 1` ele
    // lê o valor de agora -- a mesma armadilha que já derrubou um campo de
    // formulário aqui antes.
    setTeclasDigitadas((n) => n + 1);
  };

  const handleNewMessageChange = (event) => {
    setNewMessage(event.target.value);
  };

  const handleSendMessage = async (t) => {
    if (status === 0) {
      setStatus(2);
    }

    if (status === 1) {
      await setSendNewMessage(true);
      setNewMessage("");
      setSendNewMessage(false);
    }
  };

  const codeErrorCallback = (errorMessage) => {
    setCodeError(errorMessage);
  };

  const nameErrorCallback = (errorMessage) => {
    setNameError(errorMessage);
  };

  // `getCurrentTime()` morava aqui e sumiu junto com a comparação de horário
  // feita neste navegador: quem decide se está aberto agora é o servidor, no
  // fuso da empresa. Ver o comentário em `handleLogin`.

  const handleLogin = async (name, code, serviceGroupId) => {
    dispatch(setLoading());
    // QUEM DECIDE SE ESTÁ ABERTO É O SERVIDOR.
    //
    // Antes a decisão era feita aqui, comparando o relógio DESTE aparelho com
    // `open_hours[0]`. Três defeitos vinham disso, e os três sumiram ao
    // perguntar em vez de calcular:
    //
    //   1. Dia marcado como "Fechado" abria o chat. Com os horários nulos,
    //      `"14:00:00" < null` é falso E `"14:00:00" > null` também — as duas
    //      pontas passavam.
    //   2. Dia sem linha cadastrada travava aqui: `open_hours[0]` de um array
    //      vazio estoura, e não havia `try/catch` — spinner eterno.
    //   3. O relógio era o do visitante. Um celular com fuso errado, ou um
    //      cliente viajando, abria ou fechava o atendimento da empresa.
    //
    // A regra agora vive em `HorarioDeAtendimento` na api, e vale também para
    // quem chama a API direto, sem passar por esta tela.
    let horario;
    try {
      horario = await httpClient.get(`/groups/hours/${serviceGroupId || 1}`);
    } catch (e) {
      // O departamento pode não existir (o `|| 1` abaixo pede o grupo 1, que
      // não existe em empresa diferente da primeira). Antes isso derrubava a
      // tela; agora vira "fora do horário", que é o desfecho honesto: não há
      // atendimento configurado para atender.
      setStatus(2);
      dispatch(setNotLoading());
      return;
    }

    /*
     * A saudação do departamento escolhido (Gisele, 19/10/2026), guardada
     * aqui para o `ChatMessages` enviar ao abrir a conversa. Vem no mesmo
     * payload do horário -- é a mesma pergunta, "o que este departamento diz
     * a quem chega?" --, e por isso não custa uma ida extra ao servidor.
     *
     * Guardar ANTES do desvio de fora-do-horário é de propósito: quem voltar
     * dentro do expediente já entra com a saudação certa. E guardar sempre,
     * inclusive quando vem nula, é o que APAGA a do departamento anterior --
     * senão quem entra pelo Suporte depois de ter entrado pelo Financeiro
     * leria a saudação do Financeiro.
     */
    guardarSaudacao(horario.data.mensagem_de_boas_vindas);

    if (horario.data.aberto === false) {
      // A mensagem já vem com os horários de hoje no lugar dos marcadores --
      // a troca também era feita aqui, e com `open_hours[0]` vazio ela
      // mostrava `{horario_inicio}` cru para o cliente.
      if (horario.data.mensagem_fora_do_horario) {
        setOutOfHoursMessage(horario.data.mensagem_fora_do_horario);
      }
      setStatus(2);
      dispatch(setNotLoading()); // sem isso o spinner ficava por cima (parecia carregando)
      return;
    }

    await httpClient
      .post('/clients', { name: name, code: code, service_group_id: serviceGroupId || null })
      .then(response => {
        
        setRoomId(response.data.room.room_id);
        setStatus(1);
        setUser(response.data)
        setShowEndChatPrompt(false);
        setDisableChat(false);
        
        localStorage.setItem('user', JSON.stringify(response.data));
        
        const userInputData = { name: name, code: code };
        localStorage.setItem('userInput', JSON.stringify(userInputData));
        dispatch(setNotLoading());
      })
      .catch(err => {
        console.log(err)

        // Sem isto o spinner ficava girando para sempre em qualquer falha:
        // setNotLoading() so era chamado no caminho de sucesso.
        dispatch(setNotLoading());

        // `msg` so existe quando quem recusou foi o validador do Laravel.
        // A recusa por registro inexistente devolve `message`, e ler
        // `.code` de undefined estourava um TypeError aqui dentro -- o
        // catch morria e a pessoa nao via mensagem nenhuma.
        const dados = (err.response && err.response.data) || {};
        const msg = dados.msg || {};

        // O servidor também recusa fora do horário, e essa recusa tem de
        // mostrar a tela de fora-do-expediente -- não o erro de registro.
        //
        // Ela acontece mesmo com a conferência acima tendo passado: entre
        // perguntar "está aberto?" e enviar o formulário, o relógio pode ter
        // passado do fechamento. Sem este desvio, a pessoa leria "não
        // conseguimos localizar o seu registro", que é mentira e manda ela
        // conferir um dado que está certo.
        if (dados.fora_do_horario) {
          if (dados.mensagem_fora_do_horario) {
            setOutOfHoursMessage(dados.mensagem_fora_do_horario);
          }
          setStatus(2);
          return;
        }

        if (err.response && err.response.status === 422 && msg.code) {
          codeErrorCallback('Registro inválido');
        } else {
          codeErrorCallback('Infelizmente não conseguimos localizar o seu registro, verifique se o registro está correto');
        }

        if (msg.name === "The name must be a string.") {
          nameErrorCallback('O nome é obrigatório');
        }
      });
  };

  const handleLogOut = async (type) => {
    if (type === 'byOperator')
      setStatus(4);
    if (type === 'byClient') {
      dispatch(setLoading());
      setShowEndChatPrompt(false);
      try {
        setSendNewMessage(true);
        setNewMessage(`Conversa encerrada pelo Cliente: ${user.name}`);
        setTimeout(async () => {
          await httpClient.put(`/rooms/${roomId}`, {
            status: 0, user_id: operatorId, closed_by: 'client',
            client: {
              ...user, id: Object.keys(roomInfo).length !== 0 ? roomInfo.operatorId : operatorId,
              CRM_id: Object.keys(roomInfo).length !== 0 ? roomInfo.CRM_id : CRM_id
            }
          })
          setNewMessage('');
          setStatus(4);
          localStorage.removeItem('user');
          setSendNewMessage(false);
          dispatch(setNotLoading());
        }, 500);
      } catch (error) {
        console.log(error);
      }
    }
  };


  const handleEvaluation = evaluation => {
    httpClient
      .put(`/rooms/${roomId}`, {
        // client: { ...user, id: roomInfo ? roomInfo.operatorId : operatorId, CRM_id: roomInfo ? roomInfo.CRM_id : CRM_id },
        evaluation: evaluation
      })
      .then(response => {
        // Vai para a tela de agradecimento + comentário (status 5) em vez de voltar
        // direto para a tela inicial. O roomId é mantido para registrar o comentário.
        setStatus(5);
      })
      .catch(error => console.log(error));
  };

  const finishAfterEvaluation = () => {
    setStatus(3);
    setRoomId(null);
    localStorage.removeItem('roomInfo');
  };

  const handleSubmitComment = async (comment) => {
    if (comment) {
      try {
        await httpClient.put(`/rooms/${roomId}`, {
          evaluation_comment: comment
        });
      } catch (error) {
        console.log(error);
      }
    }
    finishAfterEvaluation();
  };

  const handleSendFile = (file, caption = '') => {
    let formData = new FormData();

    setSendingFile(true);

    // Se o arquivo não tiver nome (ex: Blob sem nome), usar um nome padrão
    const fileName = file.name || `file_${Date.now()}.${file.type?.split('/')[1] || 'bin'}`;
    const isImage = (file.type || '').startsWith('image/');
    const trimmedCaption = (caption || '').trim();
    // Para imagem, o texto digitado no preview vira a LEGENDA (estilo WhatsApp):
    // vai no campo message e é exibido junto da imagem, numa mensagem só — em vez
    // de ser enviado como uma mensagem de texto separada.
    formData.set('message', isImage ? trimmedCaption : fileName);
    formData.set('file', file);
    formData.set('room_id', roomId);
    formData.set('type', 0);

    httpClient
      .post('/messages/file', formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      })
      .then(response => {
        setSendingFile(false);
      })
      .catch(error => {
        console.log(error);
        setSendingFile(false);
      });
  }

  const handleChat = () => {
    if (status === 0) {
      return <>
        <div className="messages-container">
          <ChatGiveMessage name={''} />
        </div>
        <ChatNewMessage
          newMessage={newMessage}
          handleNewMessageChange={handleNewMessageChange}
          handleSendMessage={handleSendMessage}
          handleSendingMessage={handleSendingMessage}
        />
      </>
    }

    if (status === 1) {
      // Só renderiza o ChatMessage se roomId for válido
      if (!roomId || roomId === 'undefined' || roomId === 'null') {
        console.warn('Invalid roomId, showing loading...', roomId);
        return <Spinner />;
      }

      return <>
        <div className="messages-container">
          <ChatMessage
            line={1}
            roomId={roomId} status={status}
            message={newMessage}
            teclasDigitadas={teclasDigitadas}
            sendNewMessage={sendNewMessage}
            sendingFile={sendingFile}
            setDisableChat={setDisableChat}
            setNewMessage={setNewMessage}
            setSendNewMessage={setSendNewMessage}
            setShowEndChatPrompt={setShowEndChatPrompt}
            showEndChatPrompt={showEndChatPrompt}
            handleLogOut={handleLogOut}
          />
        </div>
        <div>
          <ChatNewMessage
            newMessage={newMessage}
            status={status}
            disableChat={disableChat}
            sendFile={handleSendFile}
            setNewMessage={setNewMessage}
            handleNewMessageChange={handleNewMessageChange}
            handleSendMessage={handleSendMessage}
            handleSendingMessage={handleSendingMessage}
          />
        </div>
      </>
    }

    if (status === 2) {
      return <>
        <div className="messages-container">
          <ChatMessageGiven message={outOfHoursMessage} />
        </div>
      </>
    }

    if (status === 3) {
      return <ChatLogin handleLogin={handleLogin} codeErrorCallback={codeErrorCallback}
        codeError={codeError} nameErrorCallback={nameErrorCallback} nameError={nameError} />
    }

    if (status === 4) {
      return <>
        <div className="messages-container">
          <ChatEvaluation handleEvaluation={handleEvaluation} />
        </div>
      </>
    }

    if (status === 5) {
      return <>
        <div className="messages-container">
          <ChatThankYou onSubmit={handleSubmitComment} onSkip={finishAfterEvaluation} />
        </div>
      </>
    }
  };

  useEffect(() => {
    if (user && user.room.status === 0)
      localStorage.removeItem('user')
    // eslint-disable-next-line
  }, [])

  // Slug inválido/desconhecido ou empresa suspensa (400/404 de
  // ResolveTenantFromChat, capturado pelo interceptor em assets/api/http-client.js)
  // -- mostra mensagem amigável em vez de qualquer tela de chat/login.
  if (tenantError) {
    return (
      <Grid container direction='column' alignContent={"center"} className={styles.chatRoomContainer} wrap="nowrap">
        <TenantUnavailable message={tenantError} />
      </Grid>
    );
  }

  return (
    <Grid container direction='column' alignContent={"center"} className={styles.chatRoomContainer} wrap="nowrap">
      <ChatHeader status={status} handleLogOut={handleLogOut} showEndChatPrompt={showEndChatPrompt} setStatus={setStatus} />
      {handleChat()}
      <Spinner />
    </Grid>
  );
};


export default ChatRoom;